import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, Store, ExternalLink, Activity, Search, Filter, Mail, Phone, Calendar, ToggleLeft, ToggleRight, Trash2, LogIn, ChevronDown, ChevronUp, BarChart3, ShoppingCart, IndianRupee, LogOut, Bell, RefreshCw, CreditCard, Palette, Globe, ImageIcon, CheckCircle2 } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import Loading from '../../components/Loading';
import ConfirmModal from '../../components/ConfirmModal';

export default function SuperAdminDashboard() {
    const [restaurants, setRestaurants] = useState([]);
    const [franchises, setFranchises] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [search, setSearch] = useState('');
    const [expandedId, setExpandedId] = useState(null);
    const [stats, setStats] = useState({});
    const [statsLoading, setStatsLoading] = useState({});
    const [activeTab, setActiveTab] = useState('overview');
    const [switchingId, setSwitchingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [statusUpdatingId, setStatusUpdatingId] = useState(null);
    const [confirmState, setConfirmState] = useState({ open: false, title: '', message: '', onConfirm: null });
    
    // Platform subscription setup state
    const [platformSettings, setPlatformSettings] = useState({
        cfAppId: '', cfSecretKey: '', cfEnv: 'sandbox',
        rzpKeyId: '', rzpKeySecret: '', rzpEnv: 'sandbox'
    });
    const [savingSettingsProvider, setSavingSettingsProvider] = useState(null);

    const fetchPlatformSettings = async () => {
        try {
            const res = await api.get('/api/restaurants/platform-settings');
            setPlatformSettings({
                cfAppId: res.data.cashfree?.appId || res.data.appId || '',
                cfSecretKey: res.data.cashfree?.secretKey || res.data.secretKey || '',
                cfEnv: res.data.cashfree?.environment || res.data.environment || 'sandbox',
                rzpKeyId: res.data.razorpay?.keyId || '',
                rzpKeySecret: res.data.razorpay?.keySecret || '',
                rzpEnv: res.data.razorpay?.environment || 'sandbox'
            });
        } catch (err) {
            console.error('Failed to load platform settings', err);
        }
    };

    const handleSavePlatformSettings = async (e, provider) => {
        e.preventDefault();
        setSavingSettingsProvider(provider);
        try {
            const payload = provider === 'razorpay' ? {
                provider: 'razorpay',
                keyId: platformSettings.rzpKeyId,
                keySecret: platformSettings.rzpKeySecret,
                environment: platformSettings.rzpEnv
            } : {
                provider: 'cashfree',
                appId: platformSettings.cfAppId,
                secretKey: platformSettings.cfSecretKey,
                environment: platformSettings.cfEnv
            };

            await api.post('/api/restaurants/platform-settings', payload);
            toast.success(`Platform ${provider === 'razorpay' ? 'Razorpay' : 'Cashfree'} settings verified & saved!`);
            fetchPlatformSettings();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save settings');
        } finally {
            setSavingSettingsProvider(null);
        }
    };

    // Design Requests state
    const [designRequests, setDesignRequests] = useState([]);
    const [loadingRequests, setLoadingRequests] = useState(false);
    const [updatingRequest, setUpdatingRequest] = useState(null);
    const [mappingUrl, setMappingUrl] = useState({ id: null, url: '' });

    const toast = useToast();
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // Plan settings override states
    const [selectedRestOverride, setSelectedRestOverride] = useState('');
    const [overrideForm, setOverrideForm] = useState({
        plan_name: 'Starter',
        subscription_status: 'trialing',
        trial_end: ''
    });
    const [savingOverride, setSavingOverride] = useState(false);

    // Aaharam Sites states
    const [siteUsers, setSiteUsers] = useState([]);
    const [sitesSearch, setSitesSearch] = useState('');
    const [selectedSiteUserOverride, setSelectedSiteUserOverride] = useState('');
    const [siteOverrideForm, setSiteOverrideForm] = useState({
        plan_name: 'Free',
        subscription_status: 'trialing',
        trial_end: ''
    });
    const [savingSiteOverride, setSavingSiteOverride] = useState(false);
    const [loadingSiteUsers, setLoadingSiteUsers] = useState(false);
    const [impersonatingUserId, setImpersonatingUserId] = useState(null);

    const [adminTemplates, setAdminTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);

    const fetchSiteUsers = async () => {
        setLoadingSiteUsers(true);
        try {
            const res = await api.get('/api/sites/admin/users');
            setSiteUsers(res.data || []);
        } catch (err) {
            console.error('Failed to load site users', err);
        } finally {
            setLoadingSiteUsers(false);
        }
    };

    const fetchTemplates = async () => {
        setLoadingTemplates(true);
        try {
            const res = await api.get('/api/sites/admin/templates');
            setAdminTemplates(res.data || []);
        } catch (err) {
            console.error('Failed to load admin templates', err);
        } finally {
            setLoadingTemplates(false);
        }
    };

    const handleDeleteTemplate = async (templateId, templateName) => {
        if (!window.confirm(`Are you sure you want to delete template "${templateName}"?`)) return;
        try {
            await api.delete(`/api/sites/admin/templates/${templateId}`);
            toast.success(`Template "${templateName}" deleted successfully.`);
            fetchTemplates();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete template');
        }
    };

    useEffect(() => {
        if (selectedSiteUserOverride) {
            const u = siteUsers.find(user => user.id === parseInt(selectedSiteUserOverride));
            if (u) {
                setSiteOverrideForm({
                    plan_name: u.plan_name || 'Free',
                    subscription_status: u.subscription_status || 'trialing',
                    trial_end: u.trial_end ? u.trial_end.substring(0, 10) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().substring(0, 10)
                });
            }
        }
    }, [selectedSiteUserOverride, siteUsers]);

    const handleSaveSiteOverride = async (e) => {
        e.preventDefault();
        if (!selectedSiteUserOverride) return;
        setSavingSiteOverride(true);
        try {
            await api.patch(`/api/sites/admin/users/${selectedSiteUserOverride}/plan`, siteOverrideForm);
            toast.success('Site User plan overridden successfully!');
            fetchSiteUsers();
            setSelectedSiteUserOverride('');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to override plan settings');
        } finally {
            setSavingSiteOverride(false);
        }
    };

    const handleImpersonateSiteUser = async (userId, userName) => {
        setImpersonatingUserId(userId);
        try {
            const res = await api.post(`/api/sites/admin/users/${userId}/impersonate`);
            const { token } = res.data;
            
            // Impersonation Login: Store the token as site_token and open dashboard in new tab
            localStorage.setItem('site_token', token);
            window.open('/sites/dashboard', '_blank');
            toast.success(`Accessing ${userName}'s Sites dashboard...`);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to impersonate site user');
        } finally {
            setImpersonatingUserId(null);
        }
    };

    useEffect(() => {
        if (selectedRestOverride) {
            const rest = restaurants.find(r => r.id === parseInt(selectedRestOverride));
            if (rest) {
                setOverrideForm({
                    plan_name: rest.plan_name || 'Starter',
                    subscription_status: rest.subscription_status || 'trialing',
                    trial_end: rest.trial_end ? rest.trial_end.substring(0, 10) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().substring(0, 10)
                });
            }
        }
    }, [selectedRestOverride, restaurants]);

    const handleSaveOverride = async (e) => {
        e.preventDefault();
        if (!selectedRestOverride) return;
        setSavingOverride(true);
        try {
            await api.patch(`/api/restaurants/${selectedRestOverride}/subscription-override`, overrideForm);
            toast.success('Restaurant plan settings overridden successfully!');
            fetchRestaurants();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to override plan settings');
        } finally {
            setSavingOverride(false);
        }
    };

    const fetchFranchises = async () => {
        try {
            const res = await api.get('/api/franchises/all');
            setFranchises(res.data || []);
        } catch (err) {
            console.error('Failed to load franchises', err);
        }
    };

    const handleDeleteFranchise = (id, name) => {
        setConfirmState({
            open: true,
            title: 'Delete Franchise',
            message: `Are you sure you want to permanently delete the franchise brand "${name}"? This will remove all associated branch restaurants, users, orders, menus, and data permanently. This action cannot be undone.`,
            onConfirm: async () => {
                setConfirmState({ open: false, title: '', message: '', onConfirm: null });
                try {
                    await api.delete(`/api/franchises/${id}`);
                    toast.success('Franchise and branches deleted permanently');
                    setFranchises(prev => prev.filter(f => f.id !== id));
                    fetchRestaurants();
                } catch (err) {
                    toast.error(err.response?.data?.message || 'Failed to delete franchise');
                }
            }
        });
    };

    useEffect(() => {
        fetchRestaurants();
        fetchPlatformSettings();
        fetchDesignRequests();
        fetchFranchises();
        fetchSiteUsers();
        fetchTemplates();
    }, []);

    const fetchDesignRequests = async () => {
        setLoadingRequests(true);
        try {
            const res = await api.get('/api/restaurants/design-requests');
            setDesignRequests(res.data);
        } catch (err) {
            console.error('Failed to load design requests', err);
        } finally {
            setLoadingRequests(false);
        }
    };

    const handleUpdateDesignRequest = async (id, data) => {
        setUpdatingRequest(id);
        try {
            await api.patch(`/api/restaurants/design-requests/${id}`, data);
            toast.success('Request updated successfully');
            fetchDesignRequests();
            fetchRestaurants();
        } catch (err) {
            toast.error('Failed to update request');
        } finally {
            setUpdatingRequest(null);
        }
    };

    const handleMapCustomUrl = async (id, url) => {
        try {
            await api.patch(`/api/restaurants/${id}/custom-frontend`, { custom_frontend_url: url });
            toast.success('Custom URL mapped successfully');
            setMappingUrl({ id: null, url: '' });
            fetchRestaurants();
        } catch (err) {
            toast.error('Failed to map URL');
        }
    };


    const fetchRestaurants = async (isManual = false) => {
        if (isManual) setRefreshing(true);
        try {
            const res = await api.get('/api/restaurants/all');
            setRestaurants(res.data);
            await fetchFranchises();
            await fetchSiteUsers();
            await fetchTemplates();
            if (isManual) toast.success('Platform data refreshed');
        } catch (err) {
            toast.error('Failed to load restaurants');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const fetchStats = async (id) => {
        if (stats[id]) return;
        setStatsLoading(prev => ({ ...prev, [id]: true }));
        try {
            const res = await api.get(`/api/restaurants/${id}/stats`);
            setStats(prev => ({ ...prev, [id]: res.data }));
        } catch (err) {
            console.error('Failed to load stats for', id);
        } finally {
            setStatsLoading(prev => ({ ...prev, [id]: false }));
        }
    };

    const toggleExpand = (id) => {
        if (expandedId === id) {
            setExpandedId(null);
        } else {
            setExpandedId(id);
            fetchStats(id);
        }
    };

    const handleToggleStatus = async (id, currentStatus) => {
        setStatusUpdatingId(id);
        try {
            await api.patch(`/api/restaurants/${id}/status`, { is_active: !currentStatus });
            toast.success(`Restaurant ${!currentStatus ? 'activated' : 'deactivated'}`);
            setRestaurants(prev => prev.map(r => r.id === id ? { ...r, is_active: !currentStatus } : r));
        } catch (err) {
            toast.error('Failed to toggle status');
        } finally {
            setStatusUpdatingId(null);
        }
    };

    const handleDelete = (id, name) => {
        setConfirmState({
            open: true,
            title: 'Delete Restaurant',
            message: `Are you sure you want to permanently delete "${name}"? This will remove all users, orders, menu items, and data associated with this restaurant. This action cannot be undone.`,
            onConfirm: async () => {
                setDeletingId(id);
                setConfirmState({ open: false, title: '', message: '', onConfirm: null });
                try {
                    await api.delete(`/api/restaurants/${id}`);
                    toast.success('Restaurant deleted permanently');
                    setRestaurants(prev => prev.filter(r => r.id !== id));
                } catch (err) {
                    toast.error('Failed to delete restaurant');
                } finally {
                    setDeletingId(null);
                }
            }
        });
    };

    const getTenantUrl = (slug) => {
        const host = window.location.host;
        if (host.includes('localhost') || host.includes('127.0.0.1')) {
            return `http://${slug}.localhost:5173`;
        }
        return `https://${slug}.aaharamcloud.com`;
    };
    
    const getTenantDisplay = (slug) => {
        const host = window.location.host;
        if (host.includes('localhost') || host.includes('127.0.0.1')) {
            return `${slug}.localhost:5173`;
        }
        return `${slug}.aaharamcloud.com`;
    };

    const handleSwitch = async (id) => {
        setSwitchingId(id);
        try {
            const res = await api.post(`/api/restaurants/${id}/switch`);
            const { token, restaurant } = res.data;
            
            // MAGIC LOGIN: Open restaurant dashboard with token in URL
            const magicUrl = `${getTenantUrl(restaurant.slug)}/login?token=${token}`;
            window.open(magicUrl, '_blank');
            toast.success(`Accessing ${restaurant.name} as Admin...`);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to switch');
        } finally {
            setSwitchingId(null);
        }
    };

    const handleLogout = () => {
        logout();
        window.location.href = '/login';
    };

    const filteredRestaurants = restaurants.filter(r =>
        r.name.toLowerCase().includes(search.toLowerCase()) ||
        r.slug.toLowerCase().includes(search.toLowerCase())
    );

    const activeRestaurants = restaurants.filter(r => r.is_active);
    const totalActive = activeRestaurants.length;
    const totalInactive = restaurants.length - totalActive;

    // Remove the blocking full-page loader to make the dashboard feel faster.
    // Instead, we'll show loading states inside the specific components.

    return (
        <div className="min-h-screen flex super-admin-theme text-slate-200 transition-colors duration-300" style={{ background: 'linear-gradient(135deg, #0f0c1b 0%, #05050a 100%)' }}>
            <style dangerouslySetInnerHTML={{__html: `
                @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
                .super-admin-theme {
                    font-family: 'Outfit', system-ui, -apple-system, sans-serif !important;
                    -webkit-font-smoothing: antialiased;
                }
                .super-admin-theme .bg-white {
                    background: rgba(20, 16, 38, 0.5) !important;
                    backdrop-filter: blur(24px) !important;
                    -webkit-backdrop-filter: blur(24px) !important;
                    border: 1px solid rgba(255, 255, 255, 0.06) !important;
                    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3) !important;
                }
                .super-admin-theme .text-slate-900,
                .super-admin-theme .text-gray-900,
                .super-admin-theme .text-slate-800 {
                    color: #f8fafc !important;
                }
                .super-admin-theme .text-white {
                    color: #ffffff !important;
                }
                .super-admin-theme .text-slate-500,
                .super-admin-theme .text-slate-400,
                .super-admin-theme .text-gray-500 {
                    color: #94a3b8 !important;
                }
                .super-admin-theme .text-slate-700 {
                    color: #cbd5e1 !important;
                }
                .super-admin-theme .text-slate-600,
                .super-admin-theme .text-gray-400 {
                    color: #64748b !important;
                }
                .super-admin-theme aside {
                    background: rgba(10, 8, 20, 0.8) !important;
                    backdrop-filter: blur(20px) !important;
                    border-right: 1px solid rgba(255, 255, 255, 0.05) !important;
                    box-shadow: 8px 0 32px rgba(0, 0, 0, 0.4) !important;
                }
                .super-admin-theme aside .text-white,
                .super-admin-theme aside .text-slate-900,
                .super-admin-theme aside .text-gray-900 {
                    color: #ffffff !important;
                }
                .super-admin-theme aside .text-slate-400,
                .super-admin-theme aside .text-slate-500 {
                    color: rgba(255,255,255,0.5) !important;
                }
                .super-admin-theme aside .bg-slate-800\/50 {
                    background: rgba(255,255,255,0.04) !important;
                    border: 1px solid rgba(255,255,255,0.06) !important;
                }
                .super-admin-theme aside button {
                    color: rgba(255,255,255,0.6) !important;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .super-admin-theme aside button:hover {
                    background: rgba(245, 158, 11, 0.06) !important;
                    color: #f59e0b !important;
                    transform: translateX(4px);
                }
                .super-admin-theme aside .bg-amber-500 {
                    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
                    color: #000000 !important;
                    box-shadow: 0 0 20px rgba(245, 158, 11, 0.3) !important;
                }
                .super-admin-theme aside .bg-amber-500 span,
                .super-admin-theme aside .bg-amber-500 svg {
                    color: #000000 !important;
                }
                .super-admin-theme aside .text-amber-500 {
                    color: #f59e0b !important;
                }
                .super-admin-theme aside .bg-amber-500\/20 {
                    background: rgba(245, 158, 11, 0.15) !important;
                }
                .super-admin-theme aside .bg-slate-700 {
                    background: rgba(255,255,255,0.04) !important;
                }
                .super-admin-theme aside .bg-slate-700\/50 {
                    background: rgba(255,255,255,0.02) !important;
                }
                .super-admin-theme aside .text-[10px] {
                    color: rgba(255,255,255,0.3) !important;
                }
                .super-admin-theme header {
                    background: rgba(10, 8, 20, 0.6) !important;
                    backdrop-filter: blur(20px) !important;
                    -webkit-backdrop-filter: blur(20px) !important;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
                }
                .super-admin-theme input,
                .super-admin-theme select,
                .super-admin-theme textarea {
                    background: rgba(15, 12, 30, 0.6) !important;
                    border: 1px solid rgba(255, 255, 255, 0.08) !important;
                    color: #f8fafc !important;
                }
                .super-admin-theme input:focus,
                .super-admin-theme select:focus,
                .super-admin-theme textarea:focus {
                    border-color: #f59e0b !important;
                    outline: none !important;
                    box-shadow: 0 0 15px rgba(245, 158, 11, 0.15) !important;
                }
                .super-admin-theme input::placeholder {
                    color: #475569 !important;
                }
                .super-admin-theme tr:hover {
                    background: rgba(255, 255, 255, 0.02) !important;
                }
                .super-admin-theme th {
                    background: rgba(15, 12, 30, 0.8) !important;
                    color: #94a3b8 !important;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
                    font-weight: 700 !important;
                    text-transform: uppercase !important;
                    font-size: 11px !important;
                    letter-spacing: 0.08em !important;
                }
                .super-admin-theme .border-slate-100,
                .super-admin-theme .border-slate-200,
                .super-admin-theme .divide-slate-100,
                .super-admin-theme .border-b {
                    border-color: rgba(255, 255, 255, 0.06) !important;
                }
                .super-admin-theme .divide-slate-100 > * + * {
                    border-color: rgba(255, 255, 255, 0.06) !important;
                }
                .super-admin-theme .bg-slate-50,
                .super-admin-theme .bg-gray-50 {
                    background: rgba(255, 255, 255, 0.02) !important;
                }
                .super-admin-theme .bg-slate-50\/50 {
                    background: rgba(255, 255, 255, 0.01) !important;
                }
                .super-admin-theme .shadow-xl {
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3) !important;
                }
                .super-admin-theme .rounded-\[2\.5rem\],
                .super-admin-theme .rounded-\[2rem\] {
                    border-radius: 1.5rem !important;
                }
                .super-admin-theme .rounded-3xl {
                    border-radius: 1.25rem !important;
                }
                .super-admin-theme ::-webkit-scrollbar {
                    width: 6px;
                    height: 6px;
                }
                .super-admin-theme ::-webkit-scrollbar-track {
                    background: rgba(255, 255, 255, 0.02);
                }
                .super-admin-theme ::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 10px;
                }
                .super-admin-theme ::-webkit-scrollbar-thumb:hover {
                    background: rgba(245, 158, 11, 0.4);
                }
            `}} />
            <ConfirmModal
                isOpen={confirmState.open}
                title={confirmState.title}
                message={confirmState.message}
                confirmText="Delete Permanently"
                onConfirm={confirmState.onConfirm}
                onCancel={() => setConfirmState({ open: false, title: '', message: '', onConfirm: null })}
            />

            {/* Sidebar */}
            <aside className="w-72 bg-[#0f172a] text-white min-h-screen sticky top-0 md:flex flex-col hidden border-r border-slate-800 shadow-2xl">
                <div className="p-8">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-amber-500 rounded-2xl flex items-center justify-center font-black text-gray-900 shadow-lg shadow-amber-500/20">A</div>
                        <span className="text-xl font-black tracking-tighter uppercase italic">AAHARAM<span className="text-amber-500 not-italic">.CLOUD</span></span>
                    </div>
                </div>

                <div className="px-6 py-4">
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em] mb-4 ml-2">Main Menu</p>
                    <nav className="space-y-1.5">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`w-full flex items-center space-x-3 p-3.5 rounded-2xl transition-all duration-300 group ${activeTab === 'overview' ? 'bg-amber-500 font-bold text-gray-900 shadow-lg shadow-amber-500/10' : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'}`}
                        >
                            <LayoutDashboard size={20} className={activeTab === 'overview' ? '' : 'group-hover:scale-110 transition-transform'} />
                            <span>System Overview</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('restaurants')}
                            className={`w-full flex items-center space-x-3 p-3.5 rounded-2xl transition-all duration-300 group ${activeTab === 'restaurants' ? 'bg-amber-500 font-bold text-gray-900 shadow-lg shadow-amber-500/10' : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'}`}
                        >
                            <Store size={20} className={activeTab === 'restaurants' ? '' : 'group-hover:scale-110 transition-transform'} />
                            <span>Managed Sites</span>
                            <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full ${activeTab === 'restaurants' ? 'bg-gray-900 text-amber-500' : 'bg-slate-700 text-slate-300'}`}>{restaurants.length}</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('franchises')}
                            className={`w-full flex items-center space-x-3 p-3.5 rounded-2xl transition-all duration-300 group ${activeTab === 'franchises' ? 'bg-amber-500 font-bold text-gray-900 shadow-lg shadow-amber-500/10' : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'}`}
                        >
                            <Globe size={20} className={activeTab === 'franchises' ? '' : 'group-hover:scale-110 transition-transform'} />
                            <span>Managed Franchises</span>
                            <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full ${activeTab === 'franchises' ? 'bg-gray-900 text-amber-500' : 'bg-slate-700 text-slate-300'}`}>{franchises.length}</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('subscriptions')}
                            className={`w-full flex items-center space-x-3 p-3.5 rounded-2xl transition-all duration-300 group ${activeTab === 'subscriptions' ? 'bg-amber-500 font-bold text-gray-900 shadow-lg shadow-amber-500/10' : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'}`}
                        >
                            <CreditCard size={20} className={activeTab === 'subscriptions' ? '' : 'group-hover:scale-110 transition-transform'} />
                            <span>Subscription Settings</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('design')}
                            className={`w-full flex items-center space-x-3 p-3.5 rounded-2xl transition-all duration-300 group ${activeTab === 'design' ? 'bg-amber-500 font-bold text-gray-900 shadow-lg shadow-amber-500/10' : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'}`}
                        >
                            <Palette size={20} className={activeTab === 'design' ? '' : 'group-hover:scale-110 transition-transform'} />
                            <span>Design Requests</span>
                            {designRequests.filter(r => r.status === 'pending').length > 0 && (
                                <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full ${activeTab === 'design' ? 'bg-gray-900 text-amber-500' : 'bg-amber-500/20 text-amber-500'}`}>
                                    {designRequests.filter(r => r.status === 'pending').length}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('overrides')}
                            className={`w-full flex items-center space-x-3 p-3.5 rounded-2xl transition-all duration-300 group ${activeTab === 'overrides' ? 'bg-amber-500 font-bold text-gray-900 shadow-lg shadow-amber-500/10' : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'}`}
                        >
                            <ToggleRight size={20} className={activeTab === 'overrides' ? '' : 'group-hover:scale-110 transition-transform'} />
                            <span>Plan Overrides</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('sites_users')}
                            className={`w-full flex items-center space-x-3 p-3.5 rounded-2xl transition-all duration-300 group ${activeTab === 'sites_users' ? 'bg-amber-500 font-bold text-gray-900 shadow-lg shadow-amber-500/10' : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'}`}
                        >
                            <Globe size={20} className={activeTab === 'sites_users' ? '' : 'group-hover:scale-110 transition-transform'} />
                            <span>Sites Admin</span>
                            <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full ${activeTab === 'sites_users' ? 'bg-gray-900 text-amber-500' : 'bg-slate-700 text-slate-300'}`}>{siteUsers.length}</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('sites_templates')}
                            className={`w-full flex items-center space-x-3 p-3.5 rounded-2xl transition-all duration-300 group ${activeTab === 'sites_templates' ? 'bg-amber-500 font-bold text-gray-900 shadow-lg shadow-amber-500/10' : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'}`}
                        >
                            <Palette size={20} className={activeTab === 'sites_templates' ? '' : 'group-hover:scale-110 transition-transform'} />
                            <span>Manage Templates</span>
                            <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full ${activeTab === 'sites_templates' ? 'bg-gray-900 text-amber-500' : 'bg-slate-700 text-slate-300'}`}>{adminTemplates.length}</span>
                        </button>
                    </nav>
                </div>

            </aside>

            {/* Main Content */}
            <main className="flex-1 flex flex-col min-w-0">
                {/* Top Header */}
                <header className="bg-[#0a0814]/80 border-b border-white/5 sticky top-0 z-30 px-6 md:px-10 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <h2 className="text-sm font-bold text-white hidden md:block">Active Terminal: <span className="text-amber-500 drop-shadow-[0_0_10px_rgba(245,158,11,0.2)]">PRODUCTION</span></h2>
                        <div className="h-4 w-[1px] bg-white/10 hidden md:block"></div>
                        <div className="flex items-center space-x-2">
                           <Activity size={16} className="text-emerald-500 animate-pulse drop-shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                           <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Platform Sync Active</span>
                        </div>
                    </div>

                    <div className="flex items-center space-x-4">
                        {/* Quick Switch Dropdown */}
                        <div className="relative group hidden lg:block">
                            <button className="flex items-center space-x-2 bg-white/5 border border-white/10 px-4 py-2 rounded-xl text-xs font-bold text-slate-200 hover:bg-white/10 hover:border-white/20 transition-all">
                                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                                <span>Quick Switch Site</span>
                                <ChevronDown size={14} />
                            </button>
                            <div className="absolute right-0 top-full mt-2 w-64 bg-[#0a0814]/95 border border-white/10 rounded-2xl shadow-2xl p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[100] max-h-80 overflow-y-auto backdrop-blur-xl">
                                {activeRestaurants.map(r => (
                                    <button 
                                        key={r.id}
                                        onClick={() => handleSwitch(r.id)}
                                        className="w-full flex items-center space-x-3 p-2 hover:bg-white/5 rounded-xl transition-all text-left"
                                    >
                                        <div className="w-8 h-8 bg-amber-500/10 rounded-lg flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-amber-500 border border-amber-500/20">{r.name[0]}</div>
                                        <span className="text-xs font-bold text-slate-200 truncate">{r.name}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <button className="p-2.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-all relative">
                            <Bell size={20} />
                            <span className="absolute top-2 right-2 w-2 h-2 bg-rose-500 rounded-full border-2 border-[#0a0814]"></span>
                        </button>
                        <div className="h-8 w-[1px] bg-white/10"></div>
                        
                        {/* User Profile & Sign Out Block */}
                        <div className="flex items-center space-x-3 bg-white/5 border border-white/10 p-1.5 pr-3 rounded-2xl">
                            <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-amber-500 to-orange-600 flex items-center justify-center font-black text-black text-xs shadow-md shadow-amber-500/20">SA</div>
                            <div className="hidden sm:block text-left min-w-0">
                                <p className="text-xs font-black truncate text-white leading-tight">{user?.full_name || 'System Admin'}</p>
                                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Super Admin</p>
                            </div>
                            <button
                                onClick={handleLogout}
                                className="ml-2 p-1.5 hover:bg-rose-500/10 text-slate-400 hover:text-rose-400 rounded-lg transition-colors flex items-center gap-1.5 font-bold text-xs"
                                title="Sign Out of Terminal"
                            >
                                <LogOut size={15} />
                                <span className="hidden md:inline">Sign Out</span>
                            </button>
                        </div>
                    </div>
                </header>

                <div className="p-6 md:p-10 max-w-7xl mx-auto w-full">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
                        <div>
                            <h1 className="text-3xl md:text-4xl font-extrabold text-white leading-tight tracking-tight">
                                {activeTab === 'overview' ? 'Cloud Infrastructure' : activeTab === 'restaurants' ? 'Tenant Management' : activeTab === 'franchises' ? 'Franchise Management' : activeTab === 'design' ? 'Design Requests' : activeTab === 'overrides' ? 'Plan Settings Overrides' : activeTab === 'sites_users' ? 'Sites Platform Admin' : activeTab === 'sites_templates' ? 'Sites Template Manager' : 'SaaS Subscription Settings'}
                            </h1>
                            <p className="text-slate-400 mt-2 font-medium">Control center for Aaharam Platform ecosystem.</p>
                        </div>
                        <button 
                           onClick={() => fetchRestaurants(true)}
                           disabled={refreshing}
                           className="flex items-center space-x-2 bg-white/5 border border-white/10 px-4 py-2.5 rounded-xl text-sm font-bold text-slate-200 shadow-lg hover:bg-white/10 hover:border-white/20 transition-all active:scale-95 disabled:opacity-50"
                        >
                            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
                            <span>{refreshing ? 'Syncing...' : 'Refresh Data'}</span>
                        </button>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                        {(activeTab === 'sites_users' ? [
                            { label: 'Site Users', value: siteUsers.length, sub: 'Total Builders', icon: Users, color: 'text-indigo-400', bg: 'bg-indigo-500/10 border-indigo-500/20' },
                            { label: 'Pro Sites Plan', value: siteUsers.filter(u => u.plan_name === 'Pro').length, sub: 'Unlimited tier', icon: CreditCard, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
                            { label: 'Basic Sites Plan', value: siteUsers.filter(u => u.plan_name === 'Basic').length, sub: '5-Site tier', icon: CreditCard, color: 'text-violet-400', bg: 'bg-violet-500/10 border-violet-500/20' },
                            { label: 'Free Trial Plan', value: siteUsers.filter(u => u.plan_name === 'Free').length, sub: 'Active Trials', icon: ToggleLeft, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' }
                        ] : activeTab === 'sites_templates' ? [
                            { label: 'Total Templates', value: adminTemplates.length, sub: 'Layout Catalog', icon: Palette, color: 'text-indigo-400', bg: 'bg-indigo-500/10 border-indigo-500/20' },
                            { label: 'Premium Layouts', value: adminTemplates.filter(t => t.is_premium).length, sub: 'Pro/Basic Tier', icon: CreditCard, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
                            { label: 'Free Layouts', value: adminTemplates.filter(t => !t.is_premium).length, sub: 'Open catalog', icon: ToggleRight, color: 'text-violet-400', bg: 'bg-violet-500/10 border-violet-500/20' },
                            { label: 'Template Categories', value: [...new Set(adminTemplates.map(t => t.category))].length, sub: 'Varying Styles', icon: Globe, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' }
                        ] : [
                            { label: 'Platform Tenants', value: restaurants.length, sub: 'Global Scale', icon: Store, color: 'text-indigo-400', bg: 'bg-indigo-500/10 border-indigo-500/20' },
                            { label: 'Operational Nodes', value: totalActive, sub: 'Active instances', icon: Activity, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
                            { label: 'Cold Storage', value: totalInactive, sub: 'Disabled tenants', icon: ToggleLeft, color: 'text-rose-400', bg: 'bg-rose-500/10 border-rose-500/20' },
                            { label: 'API Uptime', value: '100%', sub: 'SLA Compliant', icon: BarChart3, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' }
                        ]).map((stat, i) => (
                            <div key={i} className="bg-white p-5 rounded-2xl border border-white/5 flex flex-col items-start transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:border-white/10 group">
                                <div className="flex items-center justify-between w-full mb-4">
                                    <div className={`${stat.bg} ${stat.color} p-3 rounded-xl border`}>
                                        <stat.icon size={20} />
                                    </div>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{stat.sub}</p>
                                </div>
                                <p className="text-3xl font-extrabold text-white mb-1 tracking-tight">
                                    {loading ? <span className="inline-block w-12 h-8 bg-white/5 animate-pulse rounded-md"></span> : stat.value}
                                </p>
                                <p className="text-xs text-slate-400 font-semibold">{stat.label}</p>
                            </div>
                        ))}
                    </div>

                    {activeTab === 'subscriptions' && (
                        <div className="space-y-8">
                            {/* Platform Gateway Configurations */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* Cashfree Platform Gateway */}
                                <div className="bg-white p-8 rounded-[2rem] shadow-md border border-white/5 flex flex-col justify-between">
                                    <div>
                                        <h3 className="text-xl font-black mb-6 text-white flex items-center">
                                            <CreditCard className="mr-3 text-amber-500" /> Platform Cashfree Gateway
                                        </h3>
                                        <form onSubmit={(e) => handleSavePlatformSettings(e, 'cashfree')} className="space-y-6">
                                            <div className="space-y-4">
                                                <div className="space-y-2">
                                                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Cashfree App ID</label>
                                                    <input
                                                        required
                                                        type="text"
                                                        className="w-full bg-[#0a0814]/40 border border-white/10 rounded-xl p-4 text-white font-mono focus:ring-2 focus:ring-amber-500 text-sm"
                                                        placeholder="e.g. CF12345..."
                                                        value={platformSettings.cfAppId}
                                                        onChange={e => setPlatformSettings({...platformSettings, cfAppId: e.target.value})}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Cashfree Secret Key</label>
                                                    <input
                                                        required
                                                        type="password"
                                                        className="w-full bg-[#0a0814]/40 border border-white/10 rounded-xl p-4 text-white font-mono focus:ring-2 focus:ring-amber-500 text-sm"
                                                        placeholder="Enter Secret Key"
                                                        value={platformSettings.cfSecretKey}
                                                        onChange={e => setPlatformSettings({...platformSettings, cfSecretKey: e.target.value})}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-6">
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="radio"
                                                        id="cf-env-sandbox"
                                                        name="cf-env"
                                                        value="sandbox"
                                                        checked={platformSettings.cfEnv === 'sandbox'}
                                                        onChange={() => setPlatformSettings({...platformSettings, cfEnv: 'sandbox'})}
                                                        className="text-amber-500 focus:ring-amber-500"
                                                    />
                                                    <label htmlFor="cf-env-sandbox" className="text-xs font-bold text-slate-300">Sandbox (Test)</label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="radio"
                                                        id="cf-env-prod"
                                                        name="cf-env"
                                                        value="production"
                                                        checked={platformSettings.cfEnv === 'production'}
                                                        onChange={() => setPlatformSettings({...platformSettings, cfEnv: 'production'})}
                                                        className="text-amber-500 focus:ring-amber-500"
                                                    />
                                                    <label htmlFor="cf-env-prod" className="text-xs font-bold text-slate-300">Production (Live)</label>
                                                </div>
                                            </div>
                                            <button
                                                type="submit"
                                                disabled={savingSettingsProvider === 'cashfree'}
                                                className="w-full bg-amber-500 text-black font-black uppercase tracking-widest text-xs py-4 px-8 rounded-xl shadow-lg hover:bg-amber-600 transition-all disabled:opacity-50"
                                            >
                                                {savingSettingsProvider === 'cashfree' ? 'Verifying & Saving...' : 'Verify & Save Cashfree'}
                                            </button>
                                        </form>
                                    </div>
                                </div>

                                {/* Razorpay Platform Gateway */}
                                <div className="bg-white p-8 rounded-[2rem] shadow-md border border-white/5 flex flex-col justify-between">
                                    <div>
                                        <h3 className="text-xl font-black mb-6 text-white flex items-center">
                                            <CreditCard className="mr-3 text-indigo-400" /> Platform Razorpay Gateway
                                        </h3>
                                        <form onSubmit={(e) => handleSavePlatformSettings(e, 'razorpay')} className="space-y-6">
                                            <div className="space-y-4">
                                                <div className="space-y-2">
                                                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Razorpay Key ID</label>
                                                    <input
                                                        required
                                                        type="text"
                                                        className="w-full bg-[#0a0814]/40 border border-white/10 rounded-xl p-4 text-white font-mono focus:ring-2 focus:ring-indigo-500 text-sm"
                                                        placeholder="e.g. rzp_test_xxxxx"
                                                        value={platformSettings.rzpKeyId}
                                                        onChange={e => setPlatformSettings({...platformSettings, rzpKeyId: e.target.value})}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Razorpay Key Secret</label>
                                                    <input
                                                        required
                                                        type="password"
                                                        className="w-full bg-[#0a0814]/40 border border-white/10 rounded-xl p-4 text-white font-mono focus:ring-2 focus:ring-indigo-500 text-sm"
                                                        placeholder="Enter Key Secret"
                                                        value={platformSettings.rzpKeySecret}
                                                        onChange={e => setPlatformSettings({...platformSettings, rzpKeySecret: e.target.value})}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-6">
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="radio"
                                                        id="rzp-env-sandbox"
                                                        name="rzp-env"
                                                        value="sandbox"
                                                        checked={platformSettings.rzpEnv === 'sandbox'}
                                                        onChange={() => setPlatformSettings({...platformSettings, rzpEnv: 'sandbox'})}
                                                        className="text-indigo-400 focus:ring-indigo-500"
                                                    />
                                                    <label htmlFor="rzp-env-sandbox" className="text-xs font-bold text-slate-300">Sandbox (Test)</label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="radio"
                                                        id="rzp-env-prod"
                                                        name="rzp-env"
                                                        value="production"
                                                        checked={platformSettings.rzpEnv === 'production'}
                                                        onChange={() => setPlatformSettings({...platformSettings, rzpEnv: 'production'})}
                                                        className="text-indigo-400 focus:ring-indigo-500"
                                                    />
                                                    <label htmlFor="rzp-env-prod" className="text-xs font-bold text-slate-300">Production (Live)</label>
                                                </div>
                                            </div>
                                            <button
                                                type="submit"
                                                disabled={savingSettingsProvider === 'razorpay'}
                                                className="w-full bg-indigo-600 text-white font-black uppercase tracking-widest text-xs py-4 px-8 rounded-xl shadow-lg hover:bg-indigo-700 transition-all disabled:opacity-50"
                                            >
                                                {savingSettingsProvider === 'razorpay' ? 'Verifying & Saving...' : 'Verify & Save Razorpay'}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </div>

                            {/* Subscription List */}
                            <div className="bg-white rounded-[2.5rem] shadow-xl border border-white/5 overflow-hidden">
                                <div className="p-8 border-b border-white/5 bg-[#0a0814]/40">
                                    <h3 className="text-xl font-black text-white">Tenant Subscription Statuses</h3>
                                </div>
                                <div className="divide-y divide-white/5">
                                    {restaurants.map((r) => (
                                        <div key={r.id} className="p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 hover:bg-white/5 transition-colors text-left">
                                            <div>
                                                <h4 className="font-bold text-white">{r.name}</h4>
                                                <p className="text-xs text-slate-400 font-mono">{getTenantDisplay(r.slug)}</p>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-3">
                                                <span className={`text-xs px-3 py-1 rounded-full font-bold ${r.plan_name === 'Premium' ? 'bg-purple-500/15 text-purple-400 border border-purple-500/20' : r.plan_name === 'Professional' ? 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/20' : 'bg-blue-500/15 text-blue-400 border border-blue-500/20'}`}>
                                                    {r.plan_name || 'Starter'}
                                                </span>
                                                <span className={`text-xs px-3 py-1 rounded-full font-bold ${r.subscription_status === 'trialing' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/20' : r.subscription_status === 'active' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/15 text-rose-400 border border-rose-500/20'}`}>
                                                    {r.subscription_status || 'trialing'}
                                                </span>
                                                {r.trial_end && (
                                                    <span className="text-xs text-slate-400 font-medium">
                                                        Trial ends: {new Date(r.trial_end).toLocaleDateString()}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'design' && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="flex justify-between items-end">
                                <div>
                                    <h2 className="text-3xl font-black text-slate-900 italic">Custom Design Requests</h2>
                                    <p className="text-slate-400 font-bold text-sm">Manage one-time website design requests and deployment mappings.</p>
                                </div>
                                <button onClick={fetchDesignRequests} className="p-3 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-amber-500 transition-colors">
                                    <RefreshCw className={loadingRequests ? 'animate-spin' : ''} />
                                </button>
                            </div>

                            {loadingRequests ? (
                                <div className="py-20 text-center bg-white rounded-[2.5rem] border border-slate-100 shadow-sm">
                                    <RefreshCw className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
                                    <p className="text-slate-400 font-black uppercase tracking-widest text-xs">Syncing design requests...</p>
                                </div>
                            ) : designRequests.length === 0 ? (
                                <div className="py-20 text-center bg-white rounded-[2.5rem] border border-slate-100 shadow-sm">
                                    <Palette className="w-16 h-16 text-slate-100 mx-auto mb-4" />
                                    <p className="text-slate-400 font-bold">No design requests yet.</p>
                                </div>
                            ) : (
                                <div className="grid gap-6">
                                    {designRequests.map(req => (
                                        <div key={req.id} className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden hover:shadow-xl hover:shadow-slate-200/50 transition-all group">
                                            <div className="p-8 md:p-10">
                                                <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-6 mb-8">
                                                    <div>
                                                        <div className="flex items-center space-x-3 mb-2">
                                                            <h3 className="text-2xl font-black text-slate-900">{req.restaurant_name}</h3>
                                                            <span className={`px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                                                                req.status === 'pending' ? 'bg-amber-100 text-amber-600' : 
                                                                req.status === 'in_progress' ? 'bg-blue-100 text-blue-600' : 
                                                                req.status === 'deployed' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'
                                                            }`}>
                                                                {(req.status || 'pending').replace('_', ' ')}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center space-x-4 text-sm text-slate-400 font-bold">
                                                            <span className="flex items-center"><Mail size={14} className="mr-2" /> {req.contact_email}</span>
                                                            <span className="flex items-center"><Calendar size={14} className="mr-2" /> {new Date(req.created_at).toLocaleDateString()}</span>
                                                            <span className="text-amber-600 uppercase tracking-tighter font-black">Plan: {req.saas_plan}</span>
                                                        </div>
                                                    </div>
 
                                                    <div className="flex items-center space-x-4">
                                                        <select 
                                                            value={req.status || 'pending'}
                                                            onChange={(e) => handleUpdateDesignRequest(req.id, { status: e.target.value })}
                                                            className="bg-slate-50 border-none rounded-xl p-3 text-sm font-black text-slate-600 focus:ring-2 focus:ring-amber-500"
                                                        >
                                                            <option value="pending">Pending</option>
                                                            <option value="in_progress">In Progress</option>
                                                            <option value="deployed">Deployed & Live</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8 bg-slate-50 p-8 rounded-3xl border border-slate-100">
                                                    <div>
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Branding Colors</p>
                                                        <div className="flex space-x-3">
                                                            <div className="w-12 h-12 rounded-xl border border-slate-200 shadow-sm" style={{ backgroundColor: req.primary_color }} title="Primary"></div>
                                                            <div className="w-12 h-12 rounded-xl border border-slate-200 shadow-sm" style={{ backgroundColor: req.secondary_color }} title="Secondary"></div>
                                                            <div className="flex-1 flex items-center px-4 bg-white rounded-xl border border-slate-200 text-xs font-mono text-slate-500">
                                                                {req.primary_color} / {req.secondary_color}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="lg:col-span-2">
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Logo & References</p>
                                                        <div className="space-y-2 text-sm">
                                                            {req.logo_url && (
                                                                <a href={req.logo_url} target="_blank" rel="noreferrer" className="flex items-center text-blue-500 font-bold hover:underline">
                                                                    <ImageIcon size={16} className="mr-2" /> View Logo/Moodboard
                                                                </a>
                                                            )}
                                                            {req.reference_urls && (
                                                                <a href={req.reference_urls} target="_blank" rel="noreferrer" className="flex items-center text-blue-500 font-bold hover:underline">
                                                                    <ExternalLink size={16} className="mr-2" /> Reference URL
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="md:col-span-2 lg:col-span-3">
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Restaurant Description & Notes</p>
                                                        <p className="text-sm text-slate-700 leading-relaxed bg-white p-4 rounded-2xl border border-slate-200 whitespace-pre-wrap italic">
                                                            {req.business_description || req.notes || 'No description provided.'}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="space-y-6">
                                                    <div className="space-y-3">
                                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Mapping Deployed URL (Vercel/Netlify)</label>
                                                        <div className="flex gap-4">
                                                            <div className="relative flex-1">
                                                                <Globe className="absolute left-4 top-4 text-slate-300 w-5 h-5" />
                                                                <input 
                                                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 pl-12 focus:ring-2 focus:ring-amber-500 text-sm font-bold"
                                                                    placeholder="e.g. abc-restaurant.vercel.app"
                                                                    defaultValue={req.deployed_url || ''}
                                                                    onBlur={(e) => handleUpdateDesignRequest(req.id, { deployed_url: e.target.value, status: 'deployed' })}
                                                                />
                                                            </div>
                                                            <button 
                                                                onClick={() => window.open(req.deployed_url, '_blank')}
                                                                disabled={!req.deployed_url}
                                                                className="px-6 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest disabled:opacity-20 flex items-center"
                                                            >
                                                                <ExternalLink size={16} className="mr-2" /> Test
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-3">
                                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Admin Internal Notes</label>
                                                        <textarea 
                                                            className="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 focus:ring-2 focus:ring-amber-500 text-sm font-bold"
                                                            rows="2"
                                                            placeholder="Internal notes for our team..."
                                                            defaultValue={req.admin_notes || ''}
                                                            onBlur={(e) => handleUpdateDesignRequest(req.id, { admin_notes: e.target.value })}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'overrides' && (
                        <div className="bg-white p-8 rounded-[2rem] shadow-md border border-white/5 animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-2xl mx-auto">
                            <h3 className="text-xl font-black mb-6 text-white flex items-center">
                                <ToggleRight className="mr-3 text-amber-500 w-6 h-6" /> Tenant Plan settings Override
                            </h3>
                            
                            <form onSubmit={handleSaveOverride} className="space-y-6">
                                <div className="space-y-2 text-left">
                                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Select Restaurant</label>
                                    <select
                                        required
                                        className="w-full bg-[#0a0814]/60 border border-white/10 rounded-xl p-4 text-white focus:ring-2 focus:ring-amber-500 font-bold"
                                        value={selectedRestOverride}
                                        onChange={e => setSelectedRestOverride(e.target.value)}
                                    >
                                        <option value="" className="bg-[#0f0c1b] text-white">-- Choose a Restaurant --</option>
                                        {restaurants.map(r => (
                                            <option key={r.id} value={r.id} className="bg-[#0f0c1b] text-white">{r.name} ({r.slug})</option>
                                        ))}
                                    </select>
                                </div>

                                {selectedRestOverride && (
                                    <>
                                        <div className="grid md:grid-cols-2 gap-6 text-left">
                                            <div className="space-y-2">
                                                <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Plan Name</label>
                                                <select
                                                    required
                                                    className="w-full bg-[#0a0814]/60 border border-white/10 rounded-xl p-4 text-white focus:ring-2 focus:ring-amber-500 font-bold"
                                                    value={overrideForm.plan_name}
                                                    onChange={e => setOverrideForm({...overrideForm, plan_name: e.target.value})}
                                                >
                                                    <option value="Starter" className="bg-[#0f0c1b] text-white">Starter</option>
                                                    <option value="Professional" className="bg-[#0f0c1b] text-white">Professional</option>
                                                    <option value="Premium" className="bg-[#0f0c1b] text-white">Premium</option>
                                                </select>
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Subscription Status</label>
                                                <select
                                                    required
                                                    className="w-full bg-[#0a0814]/60 border border-white/10 rounded-xl p-4 text-white focus:ring-2 focus:ring-amber-500 font-bold"
                                                    value={overrideForm.subscription_status}
                                                    onChange={e => setOverrideForm({...overrideForm, subscription_status: e.target.value})}
                                                >
                                                    <option value="trialing" className="bg-[#0f0c1b] text-white">Trialing</option>
                                                    <option value="active" className="bg-[#0f0c1b] text-white">Active</option>
                                                    <option value="cancelled" className="bg-[#0f0c1b] text-white">Cancelled</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Trial End Date</label>
                                            <input
                                                type="date"
                                                required
                                                className="w-full bg-slate-50 border-none rounded-xl p-4 text-slate-800 focus:ring-2 focus:ring-amber-500 font-bold font-mono"
                                                value={overrideForm.trial_end}
                                                onChange={e => setOverrideForm({...overrideForm, trial_end: e.target.value})}
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={savingOverride}
                                            className="w-full bg-amber-500 text-gray-900 font-black uppercase tracking-widest text-xs py-4 px-8 rounded-xl shadow-lg hover:bg-amber-600 transition-all disabled:opacity-50"
                                        >
                                            {savingOverride ? 'Saving Override...' : 'Apply Plan Override'}
                                        </button>
                                    </>
                                )}
                            </form>
                        </div>
                    )}

                    {(activeTab === 'overview' || activeTab === 'restaurants') && (
                        /* Main List Container */
                        <div className="bg-white rounded-[2.5rem] shadow-xl border border-white/5 overflow-hidden">
                            <div className="p-6 md:p-8 border-b border-white/5 flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-[#0a0814]/40">
                                <div>
                                    <h2 className="text-2xl font-black text-white flex items-center space-x-3">
                                        <span>Cloud Inventory</span>
                                        <span className="bg-white/5 border border-white/10 text-slate-300 text-xs px-3 py-1 rounded-full shadow-sm">
                                            {filteredRestaurants.length} Sites Identified
                                        </span>
                                    </h2>
                                </div>
                                <div className="flex flex-col sm:flex-row items-center gap-3">
                                    <div className="relative w-full sm:w-80 group">
                                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5 group-focus-within:text-amber-500 transition-colors" />
                                        <input
                                            type="text"
                                            placeholder="Identify by name, slug or ID..."
                                            className="w-full pl-12 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-2xl focus:ring-4 focus:ring-amber-500/10 focus:border-amber-500 outline-none text-sm font-bold text-white transition-all"
                                            value={search}
                                            onChange={e => setSearch(e.target.value)}
                                        />
                                    </div>
                                    <button className="w-full sm:w-auto bg-white/5 border border-white/10 p-3.5 rounded-2xl text-slate-300 hover:text-white hover:bg-white/10 transition-all shadow-sm">
                                        <Filter size={20} />
                                    </button>
                                </div>
                            </div>

                            <div className="divide-y divide-white/5">
                                {loading && (
                                    <div className="p-20 text-center flex flex-col items-center">
                                        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                                        <p className="text-sm font-black text-slate-400 uppercase tracking-widest animate-pulse">Syncing Nodes...</p>
                                    </div>
                                )}

                                {!loading && filteredRestaurants.length === 0 && (
                                    <div className="p-20 text-center flex flex-col items-center">
                                        <div className="w-24 h-24 bg-white/5 rounded-[2rem] flex items-center justify-center mb-6 border border-white/5">
                                            <Store size={48} className="text-slate-500" />
                                        </div>
                                        <p className="text-xl font-black text-white">Zero Results Found</p>
                                        <p className="text-slate-400 mt-1">Try adjusting your search parameters.</p>
                                    </div>
                                )}

                                {filteredRestaurants.map((r) => (
                                    <div key={r.id} className="group/row">
                                        {/* Tenant Entry */}
                                        <div
                                            className={`flex flex-col lg:flex-row lg:items-center justify-between px-6 md:px-10 py-6 transition-all duration-300 gap-6 ${expandedId === r.id ? 'bg-amber-500/5 border-l-4 border-amber-500 pl-5 lg:pl-9' : 'hover:bg-white/5 cursor-pointer'}`}
                                            onClick={() => toggleExpand(r.id)}
                                        >
                                            <div className="flex items-center space-x-6 flex-1">
                                                <div className="w-16 h-16 bg-[#141026] border border-white/10 rounded-[1.5rem] flex-shrink-0 flex items-center justify-center font-black text-amber-500 text-2xl shadow-xl group-hover/row:scale-105 transition-transform overflow-hidden relative">
                                                    {r.logo_url ? <img src={r.logo_url} className="w-full h-full object-cover" alt="" /> : r.name[0]}
                                                    {!r.is_active && <div className="absolute inset-0 bg-slate-950/70 flex items-center justify-center text-[8px] text-rose-400 font-black tracking-widest uppercase rotate-45">Locked</div>}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center space-x-3 mb-1">
                                                        <p className="font-black text-white text-lg leading-none">{r.name}</p>
                                                        {!r.is_active && <span className="bg-rose-500/15 text-rose-400 border border-rose-500/20 text-[8px] px-1.5 py-0.5 rounded uppercase font-black tracking-tighter">Inactive</span>}
                                                    </div>
                                                    <p className="text-xs text-slate-400 font-mono font-bold">{getTenantDisplay(r.slug)}</p>
                                                    <div className="flex flex-wrap items-center gap-2 mt-2">
                                                        <span className="text-[10px] bg-white/5 border border-white/10 text-slate-400 px-2 py-0.5 rounded-lg font-black uppercase tracking-widest">ID: {String(r.id).toUpperCase()}</span>
                                                        <span className={`text-[10px] px-2 py-0.5 rounded-lg font-black uppercase tracking-widest ${r.plan_name === 'Premium' ? 'bg-purple-500/15 text-purple-400 border border-purple-500/20' : r.plan_name === 'Professional' ? 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/20' : 'bg-blue-500/15 text-blue-400 border border-blue-500/20'}`}>
                                                            Plan: {r.plan_name || 'Starter'}
                                                        </span>
                                                        <span className={`text-[10px] px-2 py-0.5 rounded-lg font-black uppercase tracking-widest ${r.subscription_status === 'trialing' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/20' : r.subscription_status === 'active' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/15 text-rose-400 border border-rose-500/20'}`}>
                                                            {r.subscription_status || 'trialing'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between lg:justify-end space-x-3 sm:space-x-4">
                                                <div className="flex items-center space-x-2">
                                                     {/* Status Switcher */}
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleToggleStatus(r.id, r.is_active); }}
                                                        disabled={statusUpdatingId === r.id}
                                                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all focus:outline-none shadow-inner ${r.is_active ? 'bg-emerald-500' : 'bg-white/10'} ${statusUpdatingId === r.id ? 'opacity-50 cursor-wait' : ''}`}
                                                    >
                                                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-all shadow-md ${r.is_active ? 'translate-x-6' : 'translate-x-1'} ${statusUpdatingId === r.id ? 'animate-pulse scale-90' : ''}`} />
                                                    </button>
                                                </div>

                                                {/* Action Bar */}
                                                <div className="flex items-center space-x-2 bg-[#141026] p-1 rounded-2xl border border-white/10 shadow-lg">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); window.open(getTenantUrl(r.slug), '_blank'); }}
                                                        className="p-2.5 text-blue-400 hover:bg-blue-500/10 rounded-xl transition-all"
                                                        title="Terminal Viewer"
                                                    >
                                                        <ExternalLink size={20} />
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleSwitch(r.id); }}
                                                        disabled={switchingId === r.id}
                                                        className={`p-2.5 rounded-xl transition-all flex items-center space-x-2 font-black text-xs ${switchingId === r.id ? 'bg-amber-500 text-black' : 'text-amber-400 hover:bg-amber-500/10 hover:scale-105'}`}
                                                        title="Authorized Impersonation"
                                                    >
                                                        {switchingId === r.id ? <RefreshCw size={18} className="animate-spin" /> : <LogIn size={20} />}
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleDelete(r.id, r.name); }}
                                                        disabled={deletingId === r.id}
                                                        className={`p-2.5 rounded-xl transition-all ${deletingId === r.id ? 'text-rose-400 bg-rose-500/10' : 'text-slate-500 hover:text-rose-400 hover:bg-rose-500/10'}`}
                                                        title="Data Purge"
                                                    >
                                                        {deletingId === r.id ? <RefreshCw size={20} className="animate-spin" /> : <Trash2 size={20} />}
                                                    </button>
                                                </div>

                                                <div className="hidden sm:block text-slate-500 pl-2">
                                                    {expandedId === r.id ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Stats Core Panel */}
                                        {expandedId === r.id && (
                                            <div className="px-6 md:px-10 pb-10 pt-2 bg-gradient-to-b from-amber-50/50 to-white/0 border-t border-slate-100 animate-in fade-in slide-in-from-top-4 duration-300">
                                                {statsLoading[r.id] ? (
                                                    <div className="flex flex-col items-center justify-center py-12 bg-white/50 rounded-[2rem] border border-slate-100 border-dashed">
                                                        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                                                        <span className="mt-4 text-xs font-black text-slate-400 uppercase tracking-widest">Hydrating Metrics...</span>
                                                    </div>
                                                ) : stats[r.id] ? (
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-6">
                                                        <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
                                                            <div className="flex items-center space-x-4">
                                                                <div className="bg-blue-500/10 text-blue-600 p-4 rounded-2xl"><Users size={22} /></div>
                                                                <div>
                                                                    <p className="text-[10px] font-black text-slate-400 uppercase mb-1">Userbase</p>
                                                                    <p className="text-2xl font-black text-slate-900">{stats[r.id].users}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
                                                            <div className="flex items-center space-x-4">
                                                                <div className="bg-emerald-500/10 text-emerald-600 p-4 rounded-2xl"><ShoppingCart size={22} /></div>
                                                                <div>
                                                                    <p className="text-[10px] font-black text-slate-400 uppercase mb-1">TX Volume</p>
                                                                    <p className="text-2xl font-black text-slate-900">{stats[r.id].orders}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
                                                            <div className="flex items-center space-x-4">
                                                                <div className="bg-amber-500/10 text-amber-600 p-4 rounded-2xl"><IndianRupee size={22} /></div>
                                                                <div>
                                                                    <p className="text-[10px] font-black text-slate-400 uppercase mb-1">Total Revenue</p>
                                                                    <p className="text-2xl font-black text-slate-900">₹{stats[r.id].revenue.toLocaleString()}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col justify-center">
                                                            <p className="text-[9px] font-black text-slate-400 uppercase mb-2">Subscription & Trial</p>
                                                            <div className="space-y-1 text-xs text-slate-700">
                                                                <div><strong className="text-slate-900">Plan:</strong> {r.plan_name || 'Starter'} ({r.billing_cycle || 'monthly'})</div>
                                                                <div><strong className="text-slate-900">Status:</strong> {r.subscription_status || 'trialing'}</div>
                                                                {r.trial_end && (
                                                                    <div><strong className="text-slate-900">Trial Ends:</strong> {new Date(r.trial_end).toLocaleDateString()}</div>
                                                                )}
                                                                {r.card_last4 && (
                                                                    <div><strong className="text-slate-900">Card Last 4:</strong> •••• {r.card_last4}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="bg-slate-900 p-6 rounded-[2rem] shadow-xl flex flex-col justify-center">
                                                            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-3">Node Metadata</p>
                                                            <div className="space-y-2">
                                                                <div className="flex items-center text-xs text-white/80"><Mail size={14} className="mr-3 text-amber-500" />{r.contact_email || 'No email registered'}</div>
                                                                <div className="flex items-center text-xs text-white/80"><Phone size={14} className="mr-3 text-amber-500" />{r.contact_phone || 'No phone registered'}</div>
                                                                <div className="flex items-center text-xs text-white/80"><Calendar size={14} className="mr-3 text-amber-500" />Live since {new Date(r.created_at).toLocaleDateString()}</div>
                                                            </div>
                                                            <div className="mt-4 pt-4 border-t border-white/5 space-y-4">
                                                                <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Custom Frontend URL</p>
                                                                {mappingUrl.id === r.id ? (
                                                                    <div className="flex space-x-2">
                                                                        <input 
                                                                            autoFocus
                                                                            className="flex-1 bg-white/10 border-none rounded-lg p-2 text-xs text-white font-mono"
                                                                            value={mappingUrl.url}
                                                                            onChange={e => setMappingUrl({...mappingUrl, url: e.target.value})}
                                                                        />
                                                                        <button onClick={() => handleMapCustomUrl(r.id, mappingUrl.url)} className="p-2 bg-amber-500 text-white rounded-lg">
                                                                            <CheckCircle2 size={14} />
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex items-center justify-between group/url">
                                                                        <span className="text-[10px] font-mono text-amber-500 truncate max-w-[150px]">
                                                                            {r.custom_frontend_url || 'Using template'}
                                                                        </span>
                                                                        <button 
                                                                            onClick={() => setMappingUrl({ id: r.id, url: r.custom_frontend_url || '' })}
                                                                            className="opacity-0 group-hover/url:opacity-100 p-1 text-slate-400 hover:text-white transition-all"
                                                                        >
                                                                            <Palette size={12} />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-6 bg-red-50 rounded-2xl border border-red-100">
                                                        <Activity size={32} className="mx-auto text-red-400 mb-2" />
                                                        <p className="text-sm font-bold text-red-600">Metric synchronization failed.</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === 'franchises' && (
                        <div className="bg-white rounded-[2.5rem] shadow-xl border border-white/5 overflow-hidden">
                            <div className="p-6 md:p-8 border-b border-white/5 bg-[#0a0814]/40">
                                <h2 className="text-2xl font-black text-white flex items-center space-x-3">
                                    <span>Registered Franchise Brands</span>
                                    <span className="bg-white/5 border border-white/10 text-slate-300 text-xs px-3 py-1 rounded-full shadow-sm">
                                        {franchises.length} Brands
                                    </span>
                                </h2>
                            </div>
                            
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-white/5">
                                    <thead>
                                        <tr>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Brand Name</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Subdomain / Slug</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Owner Details</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Branches</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Default Plan</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5">
                                        {franchises.map((f) => (
                                            <tr key={f.id} className="hover:bg-white/5 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white shadow-sm" style={{ backgroundColor: f.primary_color || '#e11d48' }}>
                                                            {f.name ? f.name[0] : 'F'}
                                                        </div>
                                                        <div className="text-left font-bold text-white">{f.name}</div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-mono text-slate-400">
                                                    <a href={getTenantUrl(f.slug)} target="_blank" rel="noreferrer" className="flex items-center text-amber-400 hover:text-amber-300 font-semibold hover:underline">
                                                        {getTenantDisplay(f.slug)}
                                                        <ExternalLink size={12} className="ml-1" />
                                                    </a>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-left text-sm">
                                                    <div className="font-bold text-white">{f.owner_name || 'N/A'}</div>
                                                    <div className="text-slate-400 text-xs">{f.owner_email || 'N/A'}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-bold text-slate-300">
                                                    {f.branch_count} outlets
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-left text-sm">
                                                    <span className={`text-xs px-2.5 py-1 rounded-full font-bold uppercase tracking-wider ${
                                                        f.default_plan === 'Premium' ? 'bg-purple-500/15 text-purple-400 border border-purple-500/20' :
                                                        f.default_plan === 'Professional' ? 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/20' :
                                                        'bg-blue-500/15 text-blue-400 border border-blue-500/20'
                                                    }`}>
                                                        {f.default_plan || 'Starter'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <button
                                                        onClick={() => handleDeleteFranchise(f.id, f.name)}
                                                        className="text-rose-400 hover:text-rose-300 p-2 hover:bg-rose-500/10 rounded-xl transition-all inline-flex items-center gap-1.5 font-bold text-xs"
                                                    >
                                                        <Trash2 size={15} />
                                                        <span>Delete</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeTab === 'sites_users' && (
                        <div className="space-y-8 animate-fade-in">
                            {/* Override Form Modal-like inline helper when a user is selected */}
                            {selectedSiteUserOverride && (
                                <div className="bg-white p-6 md:p-8 rounded-[2rem] border border-slate-200 shadow-md">
                                    <h3 className="text-lg font-black text-slate-800 mb-4 flex items-center">
                                        <ToggleRight className="mr-2 text-indigo-600 animate-pulse" /> Override Site Builder User Plan
                                    </h3>
                                    <form onSubmit={handleSaveSiteOverride} className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                                        <div>
                                            <label className="text-xs font-black text-slate-400 uppercase tracking-widest block mb-2">Target Plan</label>
                                            <select
                                                className="w-full bg-slate-50 border-none rounded-xl p-4 text-slate-800 focus:ring-2 focus:ring-amber-500 font-bold"
                                                value={siteOverrideForm.plan_name}
                                                onChange={e => setSiteOverrideForm({...siteOverrideForm, plan_name: e.target.value})}
                                            >
                                                <option value="Free">Free</option>
                                                <option value="Basic">Basic</option>
                                                <option value="Pro">Pro</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-black text-slate-400 uppercase tracking-widest block mb-2">Subscription Status</label>
                                            <select
                                                className="w-full bg-slate-50 border-none rounded-xl p-4 text-slate-800 focus:ring-2 focus:ring-amber-500 font-bold"
                                                value={siteOverrideForm.subscription_status}
                                                onChange={e => setSiteOverrideForm({...siteOverrideForm, subscription_status: e.target.value})}
                                            >
                                                <option value="trialing">Trialing</option>
                                                <option value="authenticated">Authenticated (Paid)</option>
                                                <option value="active">Active (Paid)</option>
                                                <option value="cancelled">Cancelled</option>
                                                <option value="expired">Expired</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-black text-slate-400 uppercase tracking-widest block mb-2">Trial Expiration Date</label>
                                            <input
                                                type="date"
                                                className="w-full bg-slate-50 border-none rounded-xl p-4 text-slate-800 focus:ring-2 focus:ring-amber-500 font-bold font-mono"
                                                value={siteOverrideForm.trial_end}
                                                onChange={e => setSiteOverrideForm({...siteOverrideForm, trial_end: e.target.value})}
                                            />
                                        </div>
                                        <div className="md:col-span-3 flex gap-3 justify-end mt-2">
                                            <button
                                                type="button"
                                                onClick={() => setSelectedSiteUserOverride('')}
                                                className="px-6 py-3 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 text-xs font-bold transition-all"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={savingSiteOverride}
                                                className="bg-indigo-600 hover:bg-indigo-700 text-white font-black uppercase tracking-widest text-xs py-3 px-8 rounded-xl shadow-lg transition-all disabled:opacity-50"
                                            >
                                                {savingSiteOverride ? 'Saving...' : 'Apply Override'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}

                            {/* Users Listing Table */}
                            <div className="bg-white rounded-[2.5rem] shadow-xl border border-white/5 overflow-hidden">
                                <div className="p-6 md:p-8 border-b border-white/5 bg-[#0a0814]/40 flex flex-col sm:flex-row items-center justify-between gap-4">
                                    <h2 className="text-2xl font-black text-white flex items-center space-x-3">
                                        <span>Aaharam Sites Registered Users</span>
                                        <span className="bg-white/5 border border-white/10 text-slate-300 text-xs px-3 py-1 rounded-full shadow-sm">
                                            {siteUsers.length} Users
                                        </span>
                                    </h2>
                                    
                                    {/* Search Users Input */}
                                    <div className="relative w-full sm:w-72">
                                        <input
                                            type="text"
                                            placeholder="Search site users..."
                                            className="w-full pl-10 pr-4 py-2.5 rounded-xl text-xs bg-white/5 border border-white/10 text-white focus:ring-2 focus:ring-amber-500"
                                            value={sitesSearch}
                                            onChange={e => setSitesSearch(e.target.value)}
                                        />
                                        <Search size={16} className="absolute left-3.5 top-3 text-slate-400" />
                                    </div>
                                </div>
                                
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-white/5">
                                        <thead>
                                            <tr>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">User Details</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Active Plan</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Subscription ID / Expiry</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Registered</th>
                                                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-white/5">
                                            {siteUsers
                                                .filter(u => u.name.toLowerCase().includes(sitesSearch.toLowerCase()) || u.email.toLowerCase().includes(sitesSearch.toLowerCase()))
                                                .map(u => (
                                                    <tr key={u.id} className="transition-all hover:bg-white/5">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center space-x-3">
                                                                <div className="w-9 h-9 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center font-bold text-indigo-400 text-xs">
                                                                    {u.name[0].toUpperCase()}
                                                                </div>
                                                                <div className="text-left">
                                                                    <span className="block text-xs font-black text-white">{u.name}</span>
                                                                    <span className="block text-[10px] text-slate-400">{u.email}</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                                                u.plan_name === 'Pro' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' :
                                                                u.plan_name === 'Basic' ? 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/20' :
                                                                'bg-white/5 text-slate-300 border border-white/10'
                                                            }`}>
                                                                {u.plan_name}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-left">
                                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                                                u.subscription_status === 'active' || u.subscription_status === 'authenticated' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' :
                                                                u.subscription_status === 'trialing' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/20' :
                                                                'bg-rose-500/15 text-rose-400 border border-rose-500/20'
                                                            }`}>
                                                                {u.subscription_status}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-xs font-mono text-slate-400 text-left">
                                                            <div>{u.aggregator_subscription_id || 'N/A'}</div>
                                                            {u.trial_end && (
                                                                <div className="text-[10px] text-slate-400 mt-0.5 font-sans">
                                                                    Expiry: {new Date(u.trial_end).toLocaleDateString()}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-400 text-left">
                                                            {new Date(u.created_at).toLocaleDateString()}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-right text-xs font-bold space-x-2">
                                                            <button
                                                                onClick={() => setSelectedSiteUserOverride(u.id.toString())}
                                                                className="text-indigo-400 hover:text-indigo-300 p-2 hover:bg-indigo-500/10 rounded-xl transition-all"
                                                            >
                                                                Override Plan
                                                            </button>
                                                            <button
                                                                onClick={() => handleImpersonateSiteUser(u.id, u.name)}
                                                                disabled={impersonatingUserId === u.id}
                                                                className="text-emerald-400 hover:text-emerald-300 p-2 hover:bg-emerald-500/10 rounded-xl transition-all inline-flex items-center gap-1"
                                                            >
                                                                <LogIn size={14} />
                                                                <span>{impersonatingUserId === u.id ? 'Connecting...' : 'Login as User'}</span>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'sites_templates' && (
                        <div className="space-y-8 animate-fade-in">
                            <div className="bg-[#141026]/40 rounded-[2.5rem] shadow-xl border border-white/5 overflow-hidden">
                                <div className="p-6 md:p-8 border-b border-white/5 bg-[#0a0814]/40 flex flex-col sm:flex-row items-center justify-between gap-4">
                                    <div>
                                        <h2 className="text-2xl font-black text-white flex items-center space-x-3">
                                            <span>Predefined Website Templates</span>
                                            <span className="bg-white/5 border border-white/10 text-slate-300 text-xs px-3 py-1 rounded-full shadow-sm">
                                                {adminTemplates.length} Templates
                                            </span>
                                        </h2>
                                        <p className="text-xs text-slate-400 mt-1">Design global starting points for static restaurant builder users.</p>
                                    </div>
                                    <button
                                        onClick={() => navigate('/super-admin/templates/create')}
                                        className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-wider text-xs py-3 px-6 rounded-xl shadow-md transition-all flex items-center gap-1.5"
                                    >
                                        + Design New Template
                                    </button>
                                </div>

                                <div className="p-6 md:p-8">
                                    {adminTemplates.length === 0 ? (
                                        <div className="text-center py-12 text-slate-400">
                                            <p className="text-sm font-semibold mb-2">No templates found in database.</p>
                                            <p className="text-xs">Seed initial templates or click "Design New Template" to create one.</p>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                            {adminTemplates.map(t => (
                                                <div key={t.id} className="border border-white/5 rounded-2xl overflow-hidden hover:shadow-lg transition-all bg-[#0a0814]/40 flex flex-col justify-between group">
                                                    <div>
                                                        <div className="h-44 bg-slate-950 relative overflow-hidden">
                                                            <img
                                                                src={t.thumbnail_url || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600'}
                                                                alt={t.name}
                                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                            />
                                                            {t.is_premium && (
                                                                <span className="absolute top-3 right-3 bg-amber-500 text-black text-[10px] font-black uppercase px-2.5 py-1 rounded-full tracking-wider shadow-md">
                                                                    Premium
                                                                </span>
                                                            )}
                                                            <span className="absolute top-3 left-3 bg-slate-950/80 text-white text-[9px] font-bold px-2 py-0.5 rounded-full capitalize">
                                                                {t.category}
                                                            </span>
                                                        </div>
                                                        <div className="p-5 text-left">
                                                            <h3 className="text-sm font-black text-white mb-1">{t.name}</h3>
                                                            <p className="text-xs text-slate-400 font-mono mb-2">Slug: {t.slug}</p>
                                                            <p className="text-xs text-slate-300 line-clamp-2 leading-relaxed">{t.description || 'No description provided.'}</p>
                                                        </div>
                                                    </div>

                                                    <div className="p-5 pt-0 border-t border-white/5 flex items-center justify-between mt-4">
                                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Order: {t.sort_order}</span>
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => navigate(`/super-admin/templates/editor/${t.id}`)}
                                                                className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-slate-200 border border-white/10 rounded-xl text-xs font-bold transition-all"
                                                            >
                                                                Edit Layout
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteTemplate(t.id, t.name)}
                                                                className="px-3 py-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/10 rounded-xl text-xs font-bold transition-all"
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
